import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import type { Tool } from "@/types/tool";
import { Button } from "@/components/ui/button";

interface FloatingSelectionBarProps {
  selectedTools: Tool[];
  onRemove: (id: string) => void;
  onCompare: () => void;
  onClear: () => void;
}

const FloatingSelectionBar = ({
  selectedTools,
  onRemove,
  onCompare,
  onClear,
}: FloatingSelectionBarProps) => {
  return (
    <AnimatePresence>
      {selectedTools.length > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 100, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed bottom-6 left-4 right-4 z-50 flex justify-center"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <motion.div 
            className="bg-foreground text-background rounded-2xl px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-4 shadow-xl"
            layout
            style={{ boxShadow: "0 20px 60px -10px rgba(0,0,0,0.3)" }}
          >
            <motion.span layout className="text-xs font-semibold text-background/60 flex-shrink-0 tabular-nums">
              {selectedTools.length}/4
            </motion.span>

            <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto max-w-[40vw] sm:max-w-none scrollbar-hide">
              <AnimatePresence mode="popLayout">
                {selectedTools.map((tool, index) => (
                  <motion.div
                    key={tool.id}
                    layout
                    initial={{ opacity: 0, scale: 0.5, x: -20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.5, x: 20 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25, delay: index * 0.05 }}
                    className="flex items-center gap-1.5 bg-background/10 px-2.5 py-1.5 rounded-xl flex-shrink-0"
                  >
                    <div className="w-5 h-5 rounded-md overflow-hidden bg-background/20 flex-shrink-0">
                      <img
                        src={tool.logo}
                        alt={tool.name}
                        className="w-5 h-5 object-contain"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                    <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{tool.name}</span>
                    <button
                      onClick={() => onRemove(tool.id)}
                      className="w-4 h-4 rounded-full hover:bg-background/20 flex items-center justify-center transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <motion.div layout className="w-px h-6 bg-background/20 flex-shrink-0" />

            <motion.div layout className="flex items-center gap-2.5 flex-shrink-0">
              <button
                onClick={onClear}
                className="text-xs text-background/60 hover:text-background transition-colors font-medium"
              >
                Clear
              </button>
              <Button
                onClick={onCompare}
                disabled={selectedTools.length < 2}
                size="sm"
                className="bg-background text-foreground hover:bg-background/90 rounded-xl px-4 text-xs sm:text-sm font-semibold"
              >
                Compare →
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingSelectionBar;
